import React, { useMemo, useState } from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import styled, { useTheme } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { GlobalStateContext } from "../context/global-context-provider";
import Button from "../components/button";

const NavLink = styled((props) => <Link {...props} />)`
  margin: 0 1rem;
  text-decoration: none;
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  color: var(--color-header-link);
  font-size: 1rem;
  text-transform: uppercase;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 10px 0;
    font-size: 1.2rem;
  }
`;
const HasChildren = styled.span`
  margin: 0 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 1rem;
  text-transform: uppercase;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 10px 0;
    font-size: 1.2rem;
  }
`;
const StyledMenu = styled.ul`
  transition: width 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: 40px;
  width: auto;
  padding-left: ${({ $menuPosition }) =>
    $menuPosition === "center" ? "0" : "auto"};

  a {
    color: ${({ theme, $transparentHeader, $isFrontPage }) =>
      $transparentHeader & $isFrontPage
        ? theme.colors.transparentHeaderTextColor
        : theme.colors.headerLink || theme.colors.black};
  }

  @media (max-width: 1920px) {
    padding: 0 2rem 0 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    background-color: ${({ theme }) =>
      theme.colors.headerBackground || theme.colors.secondary};
    overflow-y: scroll;
    width: ${({ burgerOpen }) => (burgerOpen ? "100vw" : "0")};
    flex-direction: column;
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    align-items: center;
    justify-content: flex-start;
    height: 100vh;
    padding: 1rem 0 7rem 0;
    margin: 0;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.16);
    transform: translateY(100%);

    a {
      color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
    }
  }
`;

const NavItem = styled.li`
  list-style-type: none;
  display: inline-block;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: ${({ burgerOpen }) => (burgerOpen ? "flex" : "none")};
    flex-direction: column;
    align-items: center;
    padding: 1rem;
    font-weight: 500;
    width: 100%;
    margin-left: 0 !important;
  }
  a {
    margin-top: 0;
    margin-bottom: 0;
  }
`;
const SubNavItem = styled.li`
  list-style-type: none;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  font-weight: bold;

  > a {
    margin-bottom: 0.5rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    font-weight: normal;
    align-items: center;
    padding: 0;

    a {
      font-size: 1rem;
    }
  }
`;
const SubSubNavItem = styled(Link)`
  font-weight: normal;
  margin: 0 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 1rem;
  padding: 5px 0;
  &:hover {
    text-decoration: underline;
  }
`;
const UlSubLinks = styled.ul`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  justify-content: flex-start;
  padding: 1rem 2rem;
  position: relative;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    flex-direction: column;
    padding-bottom: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
    &:last-child {
      padding-bottom: 1rem;
    }
  }
`;
const Dropdown = styled.div`
  max-height: 0;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  position: absolute;
  top: ${({ $headerHeight }) => `${$headerHeight}px`};
  left: 0;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  transition: all 0.5s ease;
  &.active {
    max-height: 1000px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    box-shadow: unset;
    position: unset;
    top: unset;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    &.active {
      padding: 2rem;
    }
  }
`;
const CartQuantity = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  p {
    font-weight: 600;
    margin: 0 0 0 5px;
  }
`;

const StyledIcon = styled(({ dropdownOpen: dropdownOpenIgnored, ...rest }) => (
  <FontAwesomeIcon {...rest} />
))(({ dropdownOpen }) => ({
  transform: dropdownOpen ? "rotate(180deg)" : "none",
  transition: "all 0.3s ease-in-out",
}));

const StyledButton = ({ url, label }) => {
  const theme = useTheme();
  return (
    <Button
      variant="filled"
      label={label}
      backgroundColor={
        theme.colors.headerButton
          ? theme.colors.headerButton
          : theme.colors.black
      }
      textColor={
        theme.colors.headerButtonText
          ? theme.colors.headerButtonText
          : theme.colors.white
      }
      url={url}
    />
  );
};
StyledButton.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
};

const Menu = ({
  open,
  staticQueryData,
  burgerOpen,
  transparentHeader,
  isFrontPage,
  menuPosition,
  headerHeight,
}) => {
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [totalQtyOfProducts, setTotalQtyOfProducts] = useState(0);
  const { cart } = React.useContext(GlobalStateContext);
  const dropdownRef = React.useRef();

  const handleClickOutside = (event) => {
    if (dropdownRef?.current && !dropdownRef.current.contains(event.target)) {
      setActiveDropdown(undefined);
    }
  };

  React.useEffect(() => {
    const handleEscape = (e) =>
      e.keyCode === 27 && setActiveDropdown(undefined);

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keyup", handleEscape);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keyup", handleEscape);
    };
  }, []);

  const openDropdown = (id) => {
    if (!activeDropdown || activeDropdown != id) {
      setActiveDropdown(id);
    } else {
      setActiveDropdown(null);
    }
  };

  React.useEffect(() => {
    setTotalQtyOfProducts(
      cart.reduce((totalQtyOfProducts, item) => {
        return totalQtyOfProducts + item.quantity;
      }, 0)
    );
  }, [cart]);

  React.useEffect(() => {
    if (burgerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [burgerOpen]);

  const { headerNav, wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      fragment HeaderNav on WpMenuItem {
        order
        id
        url
        cssClasses
        label
      }

      query HeaderNavQuery {
        headerNav: wpMenu(locations: { eq: HEADER_NAV }) {
          menuItems {
            nodes {
              childItems {
                nodes {
                  childItems {
                    nodes {
                      ...HeaderNav
                    }
                  }
                  ...HeaderNav
                }
              }
              parentId
              ...HeaderNav
            }
          }
        }
        wp {
          wcSettings {
            cartUrl
          }
        }
      }
    `);

  const navItems = useMemo(() => {
    return headerNav?.menuItems.nodes
      .filter((node) => node.parentId === null)
      .map((node) => {
        return {
          id: node.id,
          url: node.url,
          title: node.label,
          isButton: node.cssClasses.includes("button"),
          subLinks: node.childItems?.nodes?.map((node) => ({
            id: node.id,
            url: node.url,
            title: node.label,
            subLinks: node.childItems?.nodes?.map((node) => ({
              id: node.id,
              url: node.url,
              title: node.label,
            })),
          })),
        };
      });
  }, [headerNav]);

  const menuItems = navItems?.filter((item) => !item.isButton);
  const buttons = navItems?.filter((item) => item.isButton);
  return (
    <StyledMenu
      open={open}
      burgerOpen={burgerOpen}
      ref={dropdownRef}
      $transparentHeader={transparentHeader}
      $isFrontPage={isFrontPage}
      $menuPosition={menuPosition}
    >
      {menuItems?.map((node) => (
        <NavItem key={node.id} burgerOpen={burgerOpen}>
          {node.subLinks?.length > 0 ? (
            <HasChildren
              to=""
              activeStyle={{ fontWeight: "bold" }}
              onClick={() => openDropdown(node.id)}
            >
              {node.title}
              <StyledIcon
                icon={faAngleDown}
                size="1x"
                style={{ marginLeft: "10px" }}
                dropdownOpen={activeDropdown === node.id}
              />
            </HasChildren>
          ) : node.url === wp.wcSettings.cartUrl ? (
            <NavLink to={node.url}>
              <CartQuantity>
                <FontAwesomeIcon size="1x" icon={faShoppingCart} />
                {cart.length > 0 ? <p>{totalQtyOfProducts}</p> : ""}
              </CartQuantity>
            </NavLink>
          ) : (
            <NavLink to={node.url} activeStyle={{ fontWeight: "bold" }}>
              {node.title}
            </NavLink>
          )}
          <Dropdown
            $headerHeight={headerHeight}
            className={activeDropdown === node.id ? "active" : ""}
          >
            {node.subLinks?.length > 0 && (
              <UlSubLinks>
                {node.subLinks.map((item) => (
                  <SubNavItem key={item.id}>
                    <NavLink to={item.url}>{item.title}</NavLink>
                    {!burgerOpen &&
                      item.subLinks?.length > 0 &&
                      item.subLinks.map((item) => (
                        <SubSubNavItem to={item.url} key={item.id}>
                          {item.title}
                        </SubSubNavItem>
                      ))}
                  </SubNavItem>
                ))}
              </UlSubLinks>
            )}
          </Dropdown>
        </NavItem>
      ))}
      {buttons?.map((button) => (
        <NavItem
          burgerOpen={burgerOpen}
          key={button.id}
          style={{ marginLeft: "2rem" }}
        >
          <StyledButton url={button.url} label={button.title} />
        </NavItem>
      ))}
    </StyledMenu>
  );
};

Menu.propTypes = {
  open: PropTypes.bool.isRequired,
  burgerOpen: PropTypes.bool.isRequired,
  staticQueryData: PropTypes.object,
  transparentHeader: PropTypes.bool,
  isFrontPage: PropTypes.bool,
  menuPosition: PropTypes.string,
  headerHeight: PropTypes.string,
};

export default Menu;
